import { create } from "zustand"

import { useEffect } from "react"

interface JobAdsState {
  status: "idle" | "loading" | "success" | "error"
  records: HiBob.JobAd[]
  error: string | null
  fetchJobAds: (options?: { force?: boolean }) => Promise<void>
}

const JOB_AD_FIELDS = [
  "/jobAd/id",
  "/jobAd/applyUrl",
  "/jobAd/title",
  "/jobAd/departmentId",
  "/jobAd/department",
  "/jobAd/employmentTypeId",
  "/jobAd/employmentType",
  "/jobAd/siteId",
  "/jobAd/site",
  "/jobAd/country",
  "/jobAd/jobAdSiteId",
  "/jobAd/jobAdSite",
  "/jobAd/jobAdCountry",
  "/jobAd/languageCode",
  "/jobAd/description",
  "/jobAd/requirements",
  "/jobAd/responsibilities",
  "/jobAd/benefits",
  "/jobAd/workspaceTypeId",
  "/jobAd/workspaceType",
] as const

type RawHiBobRecord = Record<string, unknown>
type RawJobAdRecord = Record<string, { value: string }>
export type JobAdField = (typeof JOB_AD_FIELDS)[number]

const mapJobAd = (raw: RawHiBobRecord): HiBob.JobAd => {
  const rawKey = raw as RawJobAdRecord
  const val = (key: string) => rawKey[key]?.value ?? ""
  return {
    id: val("/jobAd/id"),
    applyUrl: val("/jobAd/applyUrl"),
    title: val("/jobAd/title"),
    departmentId: val("/jobAd/departmentId"),
    department: val("/jobAd/department"),
    employmentTypeId: val("/jobAd/employmentTypeId"),
    employmentType: val("/jobAd/employmentType"),
    siteId: val("/jobAd/siteId"),
    site: val("/jobAd/site"),
    country: val("/jobAd/country"),
    jobAdSiteId: val("/jobAd/jobAdSiteId"),
    jobAdSite: val("/jobAd/jobAdSite"),
    jobAdCountry: val("/jobAd/jobAdCountry"),
    languageCode: val("/jobAd/languageCode"),
    description: val("/jobAd/description"),
    requirements: val("/jobAd/requirements"),
    responsibilities: val("/jobAd/responsibilities"),
    benefits: val("/jobAd/benefits"),
    workspaceTypeId: val("/jobAd/workspaceTypeId"),
    workspaceType: val("/jobAd/workspaceType"),
  }
}

function extractRecords(json: unknown): RawHiBobRecord[] {
  if (Array.isArray(json)) return json as RawHiBobRecord[]
  if (json && typeof json === "object") {
    const obj = json as Record<string, unknown>

    if (Array.isArray(obj.items)) return obj.items as RawHiBobRecord[]
    if (Array.isArray(obj.recs)) return obj.recs as RawHiBobRecord[]
  }
  return []
}

export const useJobAdsStore = create<JobAdsState>()((set, get) => ({
  status: "idle",
  records: [],
  error: null,
  fetchJobAds: async (options) => {
    const { status } = get()
    if (status === "loading") return
    if (status === "success" && !options?.force) return

    set({ status: "loading", error: null })

    try {
      const fetchResponse = await fetch("/api/careers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fields: JOB_AD_FIELDS }),
      })

      if (!fetchResponse.ok) {
        const errorData = (await fetchResponse.json()) as {
          error?: string
          details?: string
        }
        throw new Error(
          errorData.details ??
            errorData.error ??
            `Server responded with status ${fetchResponse.status}`
        )
      }

      const recs = extractRecords(await fetchResponse.json())
      set({ status: "success", records: recs.map(mapJobAd) })
    } catch (caughtError) {
      set({
        status: "error",
        error:
          caughtError instanceof Error
            ? caughtError.message
            : "An unknown error occurred",
      })
    }
  },
}))

export const useJobAds = () => {
  const { records, status, error, fetchJobAds } = useJobAdsStore()

  useEffect(() => {
    fetchJobAds()
  }, [fetchJobAds])

  return {
    records,
    isLoading: status === "idle" || status === "loading",
    error,
    refetch: () => fetchJobAds({ force: true }),
  }
}
